import React, { useState } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  Chip,
} from "@mui/material";
import {
  VerifiedUser,
  CheckCircle,
  Handshake,
  School,
  Business,
  Security,
  Call,
} from "@mui/icons-material";
import { motion } from "framer-motion";

function PartnerServicePage() {
  const [form, setForm] = useState({
    businessName: "",
    contactPerson: "",
    phone: "",
    partnershipType: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const sendWhatsAppQuote = () => {
    if (
      !form.businessName ||
      !form.contactPerson ||
      !form.phone ||
      !form.partnershipType
    ) {
      alert("Please fill all details");
      return;
    }

    const message = `
Zolang Nexora Partnership Request

Business / Institution:
${form.businessName}

Contact Person:
${form.contactPerson}

Phone:
${form.phone}

Partnership Type:
${form.partnershipType}

Request:
We are interested in discussing partnership scope, pricing, and next steps.
`.trim();

    const whatsappNumber = "916382585971";
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;

    window.open(url, "_blank");
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
      {/* ================= HEADER ================= */}
      <Box textAlign="center" mb={8}>
        <Chip
          icon={<VerifiedUser />}
          label="Partnership & Academic Alliance"
          sx={{
            mb: 3,
            px: 2,
            fontWeight: 600,
            background: "rgba(139,0,0,0.25)",
            color: "#ffb3b3",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2.2rem", md: "3.4rem" },
              color: "#ff4d4d",
              mb: 2,
            }}
          >
            Partner With Us
          </Typography>

          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              color: "rgba(255,255,255,0.75)",
              lineHeight: 1.7,
            }}
          >
            Ethical collaboration across academia, technology, services, and
            security built on trust and long-term value.
          </Typography>
        </motion.div>
      </Box>

      {/* ================= WHY PARTNER ================= */}
      <Paper
        sx={{
          p: { xs: 3, md: 5 },
          mb: 6,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, textAlign: "center", mb: 4, color: "#ff4d4d" }}
        >
          Why Partner With Us
        </Typography>

        <Grid container spacing={3}>
          {[
            "Long-term ethical collaboration",
            "Academic and industry alignment",
            "Transparent governance",
            "Capability and system enablement",
          ].map((text, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box textAlign="center">
                <CheckCircle sx={{ fontSize: 40, color: "#ff4d4d", mb: 1 }} />
                <Typography color="white" fontWeight={600}>
                  {text}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* ================= PARTNER DOMAINS ================= */}
      <Paper
        sx={{
          p: { xs: 3, md: 5 },
          mb: 6,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 3,
        }}
      >
        <Grid container spacing={3}>
          {[
            { title: "Technology", icon: <Business /> },
            { title: "BPO & Services", icon: <VerifiedUser /> },
            { title: "Security", icon: <Security /> },
            { title: "Academy", icon: <School /> },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  background: "rgba(0,0,0,0.35)",
                  border: "1px solid rgba(255,255,255,0.15)",
                }}
              >
                <Box sx={{ color: "#ff4d4d", mb: 1 }}>{item.icon}</Box>
                <Typography fontWeight={700} color="white">
                  {item.title}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* ================= WHATSAPP FORM ================= */}
      <Paper
        sx={{
          p: { xs: 4, md: 6 },
          borderRadius: 4,
          background:
            "linear-gradient(135deg, rgba(139,0,0,0.25), rgba(0,0,0,0.6))",
          border: "2px solid rgba(139,0,0,0.5)",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={800}
          color="white"
          textAlign="center"
          mb={3}
        >
          Request Partnership & Quote
        </Typography>

        <Stack spacing={2} maxWidth={520} mx="auto">
          <TextField
            name="businessName"
            label="Business / Institution Name"
            value={form.businessName}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            name="contactPerson"
            label="Contact Person"
            value={form.contactPerson}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            name="phone"
            label="Phone Number"
            value={form.phone}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            name="partnershipType"
            label="Partnership Type (Academic / Technology / Security)"
            value={form.partnershipType}
            onChange={handleChange}
            fullWidth
          />

          <Button
            onClick={sendWhatsAppQuote}
            sx={{
              mt: 2,
              py: 1.5,
              fontWeight: 800,
              color: "#fff",
              borderRadius: 3,
              background: "linear-gradient(45deg, #8b0000, #ff4d4d)",
            }}
          >
            Send Details via WhatsApp
          </Button>
        </Stack>
      </Paper>

      {/* ================= CALL ================= */}
      <Box textAlign="center" mt={6}>
        <Button startIcon={<Call />} href="tel:+916382585971">
          Call +91 63825 85971
        </Button>
      </Box>
    </Container>
  );
}

export default PartnerServicePage;
